import { useDebouncedValue } from '@mantine/hooks'
import { cn } from '@seen/ui'
import Fuse from 'fuse.js'
import { uniqBy } from 'lodash-es'
import { type JSX, type ReactNode, useCallback, useMemo, useState } from 'react'
import { Checkbox } from '#src/components/core/Checkbox.tsx'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '#src/components/core/Command.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

export interface ComboboxAsyncOption<TValue> {
  value: TValue
  label: ReactNode
  keywords: string[]
}

// A row listed ahead of the loaded options while the search is empty: a way
// into another list, not a value of this one.
export interface ComboboxAsyncBrowseOption {
  key: string
  label: ReactNode
  onSelect: () => void
}

export interface ComboboxAsyncProps<TValue> {
  queryKey: string[]
  values?: TValue[]
  onSelect: (value: TValue | null) => void
  children: ReactNode
  loadOptions: (args: {
    input: string
    signal: AbortSignal
  }) => Promise<ComboboxAsyncOption<TValue>[]>
  searchPlaceholder?: string
  getValue: (value: TValue) => string
  localOptions?: ComboboxAsyncOption<TValue>[]
  initialSearch?: string
  initialOpen?: boolean
  // Told each time the popover opens or closes; the state stays inside.
  onOpenChange?: (open: boolean) => void
  // Modal by default; see Combobox.
  modal?: boolean
  className?: string
  // When options already come pre-filtered by the source (e.g. a server-side
  // ValueSet `filter`), Fuse should only re-rank them, never drop rows it
  // scores poorly (synonym matches whose display doesn't contain the query).
  rankOnly?: boolean
  // The source orders the options for the search; see ComboboxAsyncCommand.
  sourceRanked?: boolean
  closeOnSelect?: boolean
  // Offers a "create" row when a search finds nothing, as Combobox does.
  createLabel?: (search: string) => ReactNode
  onCreate?: (search: string) => void
  // Show the create row under the results even when some match. For lists
  // where a search usually returns near misses, which would otherwise hide it.
  alwaysShowCreate?: boolean
  // Turns the list into a checklist, like ComboboxMulti: these options render
  // checked and, before a search, sit above a divider. Selecting a row still
  // calls onSelect with its value, so the caller toggles it. Clicking the
  // checkbox keeps the popover open; clicking the label closes it.
  selected?: ComboboxAsyncOption<TValue>[]
}

export function ComboboxAsync<TValue>({
  values,
  queryKey,
  onSelect,
  children,
  loadOptions,
  getValue,
  searchPlaceholder = 'Search…',
  localOptions = [],
  initialSearch,
  initialOpen = false,
  onOpenChange,
  modal = true,
  className,
  rankOnly = false,
  sourceRanked = false,
  closeOnSelect = true,
  createLabel,
  onCreate,
  alwaysShowCreate = false,
  selected,
}: ComboboxAsyncProps<TValue>): JSX.Element {
  const [open, setOpenState] = useState(initialOpen)
  const setOpen = useCallback(
    (next: boolean): void => {
      setOpenState(next)
      onOpenChange?.(next)
    },
    [onOpenChange]
  )

  const handleSelect = useCallback(
    (value: TValue | null, keepOpen = false): void => {
      if (closeOnSelect && !keepOpen) {
        setOpen(false)
      }
      onSelect(value)
    },
    [closeOnSelect, onSelect, setOpen]
  )

  const handleCreate = useCallback(
    (search: string): void => {
      setOpen(false)
      onCreate?.(search)
    },
    [onCreate, setOpen]
  )

  return (
    <Popover open={open} onOpenChange={setOpen} modal={modal}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className={cn(className, 'p-0')} align="start">
        <ComboboxAsyncCommand
          values={values}
          queryKey={queryKey}
          onSelect={handleSelect}
          loadOptions={loadOptions}
          searchPlaceholder={searchPlaceholder}
          getValue={getValue}
          localOptions={localOptions}
          initialSearch={initialSearch}
          rankOnly={rankOnly}
          sourceRanked={sourceRanked}
          createLabel={createLabel}
          onCreate={handleCreate}
          alwaysShowCreate={alwaysShowCreate}
          selected={selected}
        />
      </PopoverContent>
    </Popover>
  )
}

interface ComboboxAsyncCommandProps<TValue> {
  values?: TValue[]
  queryKey: string[]
  onSelect: (value: TValue | null, keepOpen?: boolean) => void
  loadOptions: ComboboxAsyncProps<TValue>['loadOptions']
  searchPlaceholder?: string
  getValue: ComboboxAsyncProps<TValue>['getValue']
  localOptions?: ComboboxAsyncOption<TValue>[]
  initialSearch?: string
  rankOnly?: boolean
  // The source filters and orders the options for the search itself, so they
  // are shown as loaded, never re-ranked.
  sourceRanked?: boolean
  createLabel?: (search: string) => ReactNode
  onCreate?: (search: string) => void
  alwaysShowCreate?: boolean
  browseOptions?: ComboboxAsyncBrowseOption[]
  selected?: ComboboxAsyncOption<TValue>[]
}

export function ComboboxAsyncCommand<TValue>({
  queryKey,
  onSelect,
  loadOptions,
  getValue,
  searchPlaceholder = 'Search…',
  localOptions = [],
  browseOptions = [],
  initialSearch,
  rankOnly = false,
  sourceRanked = false,
  createLabel,
  onCreate,
  alwaysShowCreate = false,
  selected,
}: ComboboxAsyncCommandProps<TValue>): JSX.Element {
  const EMPTY_OPTIONS: ComboboxAsyncOption<TValue>[] = useMemo(() => [], [])

  const [search, setSearch] = useState(initialSearch ?? '')
  const debouncedSearch = useDebouncedCallback((value: string) => {
    setSearch(value)
  }, 50)
  const [debouncedValue] = useDebouncedValue(search, 100)

  const { data: remoteOptions = EMPTY_OPTIONS, isPending } = useQuery({
    queryKey: [...queryKey, debouncedValue],
    queryFn: ({ signal }) => loadOptions({ input: debouncedValue, signal }),
  })

  const allOptions = useMemo(
    () =>
      uniqBy([...(selected ?? []), ...localOptions, ...remoteOptions], (opt) =>
        getValue(opt.value)
      ),
    [selected, localOptions, remoteOptions, getValue]
  )
  const loadedOptionCount = useMemo(
    () =>
      uniqBy([...localOptions, ...remoteOptions], (opt) => getValue(opt.value))
        .length,
    [localOptions, remoteOptions, getValue]
  )

  const selectedValues = useMemo(
    () => new Set(selected?.map((option) => getValue(option.value))),
    [selected, getValue]
  )

  const fuse = useMemo(
    () =>
      new Fuse(allOptions, {
        keys: ['keywords'],
        sortFn: (a, b) => a.score - b.score,
      }),
    [allOptions]
  )

  const filteredOptions = useMemo(() => {
    if (!search) {
      return allOptions
    }
    // Local options are suggestions for an empty search; a ranked source
    // answers the search itself.
    if (sourceRanked) {
      return remoteOptions
    }

    const ranked = fuse.search(search).map((result) => result.item)
    if (!rankOnly) {
      return ranked
    }
    // Keep every option, ranked matches first: the source already filtered to
    // the query, so a poorly-scored row is still a valid result, not noise.
    const rankedSet = new Set(ranked)
    return [...ranked, ...allOptions.filter((option) => !rankedSet.has(option))]
  }, [allOptions, remoteOptions, fuse, search, rankOnly, sourceRanked])

  // Waits for the search to settle, so the row never flashes while results
  // are still on their way.
  const showCreateRow =
    !!createLabel &&
    search.trim().length > 1 &&
    search === debouncedValue &&
    !isPending &&
    (alwaysShowCreate || filteredOptions.length === 0)

  // Before a search, selected options render above the divider instead of in
  // the list.
  const pinnedOptions = selected && !search ? selected : []
  const visibleOptions =
    selected && !search
      ? filteredOptions.filter(
          (option) => !selectedValues.has(getValue(option.value))
        )
      : filteredOptions
  const listedOptions = visibleOptions.slice(0, MAX_OPTIONS_TO_SHOW)

  const renderOption = (option: ComboboxAsyncOption<TValue>): JSX.Element => {
    const value = getValue(option.value)
    const commandValue = value ?? COMBOBOX_NULL_OPTION
    const select = (keepOpen: boolean): void => {
      if (commandValue === COMBOBOX_NULL_OPTION) {
        onSelect(null, keepOpen)
      } else {
        onSelect(option.value, keepOpen)
      }
    }

    if (!selected) {
      return (
        <CommandItem
          key={value}
          value={commandValue}
          onSelect={() => select(false)}
          className="group items-center justify-between gap-2"
        >
          {option.label}
        </CommandItem>
      )
    }

    const isSelected = selectedValues.has(value)
    return (
      <CommandItem
        key={value}
        value={commandValue}
        onSelect={() => select(false)}
        className="group cursor-pointer items-center gap-2 p-0"
      >
        <div className="flex w-full items-center">
          <div
            className="flex items-center p-2 pr-4"
            onClick={(e) => {
              e.stopPropagation()
              select(true)
            }}
          >
            <Checkbox
              aria-label={isSelected ? 'Deselect' : 'Select'}
              className={cn(
                'text-muted-foreground hover:opacity-100! group-hover:visible',
                !isSelected && 'invisible group-hover:opacity-50'
              )}
              value={isSelected}
            />
          </div>
          <div className="flex-1 p-2 pl-0">{option.label}</div>
        </div>
      </CommandItem>
    )
  }

  return (
    <Command shouldFilter={false}>
      <CommandInput
        // We don't use the value prop because when it's used, the list scroll position doesn't reset properly when you press backspace in the input.
        onValueChange={debouncedSearch}
        placeholder={searchPlaceholder}
        className="rounded-none border-b px-3"
        autoFocus
      />
      <CommandList
        // We use a key to reset the scroll position when the search text
        // changes. It counts only loaded options, so (de)selecting doesn't
        // remount the list.
        key={`${search}-${loadedOptionCount}`}
        // Browser scroll anchoring otherwise shifts the list when an option
        // moves across the divider.
        className={cn(
          COMBOBOX_LIST_MAX_HEIGHT,
          selected && '[overflow-anchor:none]'
        )}
      >
        <CommandGroup>
          {!search &&
            browseOptions.map((option) => (
              <CommandItem
                key={option.key}
                value={option.key}
                onSelect={option.onSelect}
                className="group items-center justify-between gap-2"
              >
                {option.label}
              </CommandItem>
            ))}
          {/* One flat keyed list, so (de)selecting moves the option's node
              across the divider. A remount would make cmdk reselect and
              scroll the moved option into view. */}
          {[
            ...pinnedOptions.map(renderOption),
            ...(pinnedOptions.length > 0 && listedOptions.length > 0
              ? [<CommandSeparator key={DIVIDER_KEY} />]
              : []),
            ...listedOptions.map(renderOption),
          ]}
          {visibleOptions.length > MAX_OPTIONS_TO_SHOW && (
            <CommandItem disabled>Search to see more results…</CommandItem>
          )}
          {showCreateRow && createLabel ? (
            <CommandItem
              value={COMBOBOX_CREATE_VALUE}
              onSelect={() => onCreate?.(search)}
            >
              {createLabel(search)}
            </CommandItem>
          ) : null}
        </CommandGroup>
        <CommandEmpty>
          {isPending ? <Loader /> : 'Adjust search to see more results.'}
        </CommandEmpty>
      </CommandList>
    </Command>
  )
}

const COMBOBOX_NULL_OPTION = '__COMBOBOX_NULL_OPTION__'
const COMBOBOX_CREATE_VALUE = '__COMBOBOX_CREATE_OPTION__'

const DIVIDER_KEY = '__COMBOBOX_DIVIDER__'

const COMBOBOX_LIST_MAX_HEIGHT = 'max-h-[calc(50vh-5rem)]' // 50vh - search bar height - trigger height. 7rem is a rough estimate of the height of the search bar and trigger.

const MAX_OPTIONS_TO_SHOW = 50 // Search performance is better with fewer options and it's hard to imagine people scrolling through more than 50 options.
